import { BlockControls } from '@wordpress/block-editor';
import {
  Dropdown,
  MenuGroup,
  MenuItemsChoice,
  Modal,
  ToolbarGroup,
  ToolbarButton,
} from '@wordpress/components';
import { store as coreStore, useEntityRecord } from '@wordpress/core-data';
import { useSelect } from '@wordpress/data';
import { useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';

import { ImageVariationPicker } from '@bigbite/mediapress-components';
import { gallery } from '@wordpress/icons';

import useDomImageReplace from '../hooks/useDomImageReplace';

/**
 * A component that renders the featured image variation controls on a block.
 *
 * @param {Object}    props               Component props
 * @param {Object}    props.attributes    The block attributes
 * @param {Function}  props.setAttributes The block attributes setter
 * @param {string}    props.postType      The post type slug
 * @param {number}    props.postId        The post ID
 * @param {React.Ref} props.wrapperRef    The block wrapper ref
 * @param {Object}    props.config        The featured image variations config
 *
 * @return {JSX.Element}
 */
const FeaturedImageVariationBlockControls = ({
  attributes,
  setAttributes,
  postType,
  postId,
  wrapperRef,
  config,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { editedRecord: editedPost, edit: editPost } = useEntityRecord(
    'postType',
    postType,
    postId,
  );

  const media = useSelect((select) => select(coreStore).getMedia(editedPost?.featured_media));

  /**
   * The choices for the featured image variations dropdown.
   *
   * @type {Array<{label: string, value: string}>}
   */
  const FEATURED_IMAGE_VARIATIONS_CHOICES = [
    {
      label: __('Original', 'mediapress'),
      value: null,
    },
    ...Object.entries(config).map(([key, value]) => ({
      label: value.label,
      value: key,
    })),
  ];

  const selectedVariation = attributes.mediaPressMediaFeaturedImageVariation;
  const selectedVariationData =
    editedPost?.meta?.mediapress_media_featured_image_variations?.[selectedVariation];
  const configItem = config[selectedVariation];

  useDomImageReplace(
    wrapperRef,
    editedPost?.featured_media,
    selectedVariation,
    selectedVariationData,
  );

  /**
   * Handle the user changing the image variations.
   *
   * @param {Object} newVariations The new image variations
   */
  const handleChange = (newVariations) => {
    editPost({ meta: { mediapress_media_featured_image_variations: newVariations } });
  };

  /**
   * Handle the variation change
   *
   * @param {string} variation The new variation
   */
  const handleVariationChange = (variation) => {
    setAttributes({ mediaPressMediaFeaturedImageVariation: variation });
  };

  return (
    <>
      <BlockControls>
        <ToolbarGroup>
          {media && (
            <ToolbarButton
              icon={gallery}
              title={__('Adjust image variations', 'mediapress')}
              onClick={() => setIsModalOpen(true)}
            />
          )}
          <Dropdown
            renderToggle={({ isOpen, onToggle }) => (
              <ToolbarButton aria-expanded={isOpen} aria-haspopup="true" onClick={onToggle}>
                {configItem?.label || __('Original', 'mediapress')}
              </ToolbarButton>
            )}
            renderContent={({ onClose }) => (
              <MenuGroup label={__('Image variations', 'mediapress')}>
                <MenuItemsChoice
                  choices={FEATURED_IMAGE_VARIATIONS_CHOICES}
                  onHover={() => {}}
                  onSelect={(newValue) => {
                    handleVariationChange(newValue);
                    onClose();
                  }}
                  value={selectedVariation}
                />
              </MenuGroup>
            )}
          />
        </ToolbarGroup>
      </BlockControls>
      {isModalOpen && (
        <Modal
          title={__('Adjust image variations', 'mediapress')}
          onRequestClose={() => setIsModalOpen(false)}
          isFullScreen
        >
          <ImageVariationPicker
            config={config}
            sourceImage={media}
            variations={editedPost?.meta?.mediapress_media_featured_image_variations}
            onInit={handleChange}
            onChange={handleChange}
          />
        </Modal>
      )}
    </>
  );
};

export default FeaturedImageVariationBlockControls;
